import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { PERSONALISIERUNG_ADMIN_PATH } from '@mcikm-vote/shared';
import type {
  LogoVariante,
  PersonalisierungAntwort,
  PersonalisierungEingabe,
} from '@mcikm-vote/shared';

import { apiAnfrage } from '../api/anfrage';
import type { ApiFehler } from '../api/anfrage';
import { PERSONALISIERUNG_SCHLUESSEL } from '../personalisierung/personalisierungApi';

/**
 * Verwaltung der Personalisierung (Änderungsanforderung ÄA-5).
 *
 * Jede Änderung entwertet **beide** Zwischenspeicher: den der Verwaltungsansicht und den der
 * öffentlichen Auskunft. Ohne den zweiten zeigten Kopf und Fußzeile weiter den alten Stand,
 * obwohl das Formular bereits den neuen anzeigt.
 */

export const PERSONALISIERUNG_ADMIN_SCHLUESSEL = ['admin', 'personalisierung'] as const;

export function usePersonalisierungVerwaltung(): UseQueryResult<
  PersonalisierungAntwort,
  ApiFehler
> {
  return useQuery<PersonalisierungAntwort, ApiFehler>({
    queryKey: PERSONALISIERUNG_ADMIN_SCHLUESSEL,
    queryFn: () => apiAnfrage<PersonalisierungAntwort>(PERSONALISIERUNG_ADMIN_PATH),
  });
}

/** Legt die Antwort in beide Zwischenspeicher und entwertet sie zugleich. */
function useUebernahme(): (antwort: PersonalisierungAntwort) => void {
  const queryClient = useQueryClient();

  return (antwort) => {
    queryClient.setQueryData(PERSONALISIERUNG_ADMIN_SCHLUESSEL, antwort);
    queryClient.setQueryData(PERSONALISIERUNG_SCHLUESSEL, antwort);
    void queryClient.invalidateQueries({ queryKey: PERSONALISIERUNG_SCHLUESSEL });
  };
}

export function usePersonalisierungSpeichern(): UseMutationResult<
  PersonalisierungAntwort,
  ApiFehler,
  PersonalisierungEingabe
> {
  const uebernimm = useUebernahme();

  return useMutation<PersonalisierungAntwort, ApiFehler, PersonalisierungEingabe>({
    mutationFn: (eingabe) =>
      apiAnfrage<PersonalisierungAntwort>(PERSONALISIERUNG_ADMIN_PATH, {
        methode: 'PUT',
        koerper: eingabe,
      }),
    onSuccess: uebernimm,
  });
}

export function useLogoHochladen(): UseMutationResult<
  PersonalisierungAntwort,
  ApiFehler,
  { variante: LogoVariante; datei: File }
> {
  const uebernimm = useUebernahme();

  return useMutation<PersonalisierungAntwort, ApiFehler, { variante: LogoVariante; datei: File }>({
    mutationFn: ({ variante, datei }) => {
      // `FormData`; den Content-Type setzt der Browser samt Trennzeichenfolge selbst.
      const formular = new FormData();
      formular.append('datei', datei);

      return apiAnfrage<PersonalisierungAntwort>(
        `${PERSONALISIERUNG_ADMIN_PATH}/logo/${variante}`,
        { methode: 'PUT', formular },
      );
    },
    onSuccess: uebernimm,
  });
}

export function useLogoEntfernen(): UseMutationResult<
  PersonalisierungAntwort,
  ApiFehler,
  LogoVariante
> {
  const uebernimm = useUebernahme();

  return useMutation<PersonalisierungAntwort, ApiFehler, LogoVariante>({
    mutationFn: (variante) =>
      apiAnfrage<PersonalisierungAntwort>(`${PERSONALISIERUNG_ADMIN_PATH}/logo/${variante}`, {
        methode: 'DELETE',
      }),
    onSuccess: uebernimm,
  });
}
